import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  FileText, CheckCircle, AlertCircle, User,
  Building, Calendar, Briefcase, PenTool
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SIGNER_LABELS = {
  candidate: 'Candidate',
  sales_executive: 'Sales Executive',
  director: 'Director'
};

export default function SignOfferPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [details, setDetails] = useState(null);
  const [error, setError] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [comments, setComments] = useState('');

  useEffect(() => {
    fetchSigningDetails();
  }, [token]);

  const fetchSigningDetails = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/onboarding/sign/${token}/details`);
      setDetails(response.data);
      
      if (response.data.already_signed) {
        setError('already_signed');
      }
    } catch (error) {
      console.error('Error fetching signing details:', error);
      if (error.response?.status === 401) {
        setError('expired');
      } else {
        setError('invalid');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSign = async () => {
    if (!agreed) {
      toast.error('Please agree to the terms before signing');
      return;
    }

    setSigning(true);
    try {
      await axios.post(`${API_URL}/api/onboarding/sign/${token}`, {
        signature_data: `Signed electronically by ${details.signer_type} on ${new Date().toISOString()}`,
        comments: comments
      });
      
      toast.success('Document signed successfully!');
      setDetails({ ...details, already_signed: true });
      setError('success');
    } catch (error) {
      console.error('Error signing:', error);
      toast.error(error.response?.data?.detail || 'Failed to sign document');
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error === 'expired') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center">
          <CardContent className="p-8">
            <AlertCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />
            <h2 className="text-xl font-bold mb-2">Link Expired</h2>
            <p className="text-muted-foreground">
              This signing link has expired. Please contact HR to request a new link.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error === 'invalid') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center">
          <CardContent className="p-8">
            <AlertCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />
            <h2 className="text-xl font-bold mb-2">Invalid Link</h2>
            <p className="text-muted-foreground">
              This signing link is invalid. Please check the link and try again.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error === 'already_signed' || error === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center">
          <CardContent className="p-8">
            <CheckCircle className="w-16 h-16 mx-auto text-green-500 mb-4" />
            <h2 className="text-xl font-bold mb-2">
              {error === 'success' ? 'Signed Successfully!' : 'Already Signed'}
            </h2>
            <p className="text-muted-foreground mb-4">
              {error === 'success' 
                ? 'Thank you for signing. The next approver has been notified.'
                : 'This document has already been signed. No further action is required.'
              }
            </p>
            <p className="text-sm text-muted-foreground">
              You can close this window.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 py-8 px-4">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center">
                  <FileText className="w-8 h-8 text-primary" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold">Offer Letter</h1>
                  <p className="text-muted-foreground">
                    Sign as: <span className="font-medium text-primary">{SIGNER_LABELS[details.signer_type]}</span>
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Candidate</p>
                <p className="font-semibold">{details.candidate_name}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Offer Details Summary */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Offer Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Candidate:</span>
                <span className="font-medium">{details.candidate_name}</span>
              </div>
              <div className="flex items-center gap-2">
                <Briefcase className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Position:</span>
                <span className="font-medium">{details.position}</span>
              </div>
              <div className="flex items-center gap-2">
                <Building className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Client:</span>
                <span className="font-medium">{details.client_company}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Offer Letter Content */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Offer Letter Document</CardTitle>
          </CardHeader>
          <CardContent>
            <div 
              className="bg-white rounded-lg p-6 min-h-[500px] overflow-auto prose max-w-none"
              dangerouslySetInnerHTML={{ __html: details.offer_content }}
            />
          </CardContent>
        </Card>

        {/* Signature Section */}
        <Card className="border-primary/50">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <PenTool className="w-5 h-5" />
              Electronic Signature
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-muted/50 p-4 rounded-lg">
              <p className="text-sm text-muted-foreground mb-4">
                By checking the box below and clicking "Sign Document", you are agreeing to sign this document electronically. 
                This electronic signature has the same legal validity as a handwritten signature.
              </p>
              
              <div className="flex items-start gap-3">
                <Checkbox 
                  id="agree" 
                  checked={agreed}
                  onCheckedChange={(checked) => setAgreed(checked)}
                  data-testid="agree-checkbox"
                />
                <label htmlFor="agree" className="text-sm cursor-pointer">
                  I, <strong>{details.signer_type === 'candidate' ? details.candidate_name : SIGNER_LABELS[details.signer_type]}</strong>, 
                  have read and understood the contents of this offer letter. I agree to sign this document electronically.
                </label>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="comments">Comments (Optional)</Label>
              <Textarea
                id="comments"
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder="Add any comments or notes..."
                className="min-h-[80px]"
              />
            </div>

            <div className="flex items-center gap-3 pt-4">
              <Button 
                onClick={handleSign} 
                disabled={!agreed || signing}
                className="flex-1"
                data-testid="sign-button"
              >
                {signing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Signing...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Sign Document
                  </>
                )}
              </Button>
            </div>

            <p className="text-xs text-center text-muted-foreground">
              Signing as {SIGNER_LABELS[details.signer_type]} on {new Date().toLocaleDateString('en-GB', { 
                day: 'numeric', 
                month: 'long', 
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
